import { cn } from 'cn';
import type { TableOfContents } from 'fumadocs-core/toc';
import { useSyncExternalStore } from 'react';

import { m } from '@/paraglide/messages';

/**
 * Below this, the rail lists too little to be worth the width - several policies carry no headings
 * at all, and an empty rail reads as a bug.
 */
const minimumItems = 4;

/** Where a heading counts as reached, matching `scroll-padding-top`. */
const activeOffset = 40;

function subscribe(onChange: () => void) {
  // No throttle: scroll events are already frame-paced, and a handful of heading positions is
  // cheaper to measure than a frame queue is to keep.
  window.addEventListener('scroll', onChange, { passive: true });
  window.addEventListener('resize', onChange);
  return () => {
    window.removeEventListener('scroll', onChange);
    window.removeEventListener('resize', onChange);
  };
}

/** The last heading scrolled past, or the first one before the reader reaches any of them. */
function activeHeading(toc: TableOfContents): string | undefined {
  let active: string | undefined;
  for (const { url } of toc) {
    const element = document.getElementById(url.slice(1));
    if (!element) continue;
    if (element.getBoundingClientRect().top > activeOffset) break;
    active = url;
  }
  return active;
}

/**
 * Anchor links for a document's headings, shown beside the prose on wide screens.
 *
 * The highlight reads scroll position rather than using fumadocs' `AnchorProvider`, which marks
 * every heading in view rather than the one being read, and resolves its headings once when it
 * mounts - a lifecycle we would have to work around, since these pages render their body after the
 * route data lands.
 */
export function DocsToc({ toc }: { toc: TableOfContents }) {
  const active = useSyncExternalStore(
    subscribe,
    () => activeHeading(toc),
    () => undefined,
  );

  if (toc.length < minimumItems) return null;

  return (
    <aside className='bg-mist sticky top-6 hidden h-fit w-70 shrink-0 self-start rounded-[20px] px-2.5 py-5 lg:block'>
      <p className='text-subtle px-3.5 pb-2.5 text-[13px] font-bold tracking-[0.04em] uppercase'>{m.legal_toc()}</p>
      <nav aria-label={m.legal_toc()} className='flex flex-col'>
        {toc.map((item) => (
          <a
            key={item.url}
            href={item.url}
            aria-current={item.url === active ? 'location' : undefined}
            className={cn(
              'rounded-[10px] py-2 text-[15px] transition-colors hover:text-brand',
              item.url === active ? 'bg-[#eef3fa] font-semibold text-[#1f4a80]' : 'text-body',
              item.depth > 2 ? 'pr-3.5 pl-7' : 'px-3.5',
            )}
          >
            {item.title}
          </a>
        ))}
      </nav>
    </aside>
  );
}
